/**
 * Demo content for the /verdict three-tab product view (Answers · Brands ·
 * Sources). No scan endpoint exists yet, so this page is an interactive
 * walkthrough of the real product UI, not a live query — every number here
 * is derived from the single source of truth, DEMO_WORKSPACE
 * (lib/site-config.ts). The per-surface / per-brand / per-source detail
 * below is hand-authored illustrative content for the fictional demo brand
 * "Northstar"; it is arithmetically consistent with DEMO_WORKSPACE (ranked
 * 3 of 5 brands with 2 competitors ahead, appeared in 5 of 5 surface
 * answers, first choice in 1 of 5, 12 cited sources) — if those numbers
 * change, update the arrays below to match. Northstar is named on every
 * surface (matching the homepage's "appeared in 5 of 5" story), but with
 * weaker presence than Vantra/Lumen on four of them — that gap is what
 * carries the "you're losing" story, not absence.
 */

import { DEMO_WORKSPACE } from "@/lib/site-config"

export type PresenceStatus = "PRIMARY" | "SECONDARY" | "MENTION" | "ABSENT"

export interface SurfaceAnswer {
  surface: string
  status: PresenceStatus
  firstChoice: boolean
  named: string[]
}

// 5 surfaces, matching DEMO_WORKSPACE's 5-surface denominator (position
// "3 of 5" brands ranked; firstChoice "1 of 5 surfaces"). Northstar is
// named on all 5 — first choice on ChatGPT, then progressively weaker
// (SECONDARY/MENTION) on the rest, which is the "losing" story.
export const SURFACE_ANSWERS: SurfaceAnswer[] = [
  { surface: "ChatGPT", status: "PRIMARY", firstChoice: true, named: ["Northstar", "Vantra", "Lumen"] },
  { surface: "Gemini", status: "MENTION", firstChoice: false, named: ["Vantra", "Lumen", "Northstar", "Fieldstone"] },
  { surface: "Claude", status: "SECONDARY", firstChoice: false, named: ["Vantra", "Lumen", "Fieldstone", "Northstar"] },
  { surface: "Perplexity", status: "MENTION", firstChoice: false, named: ["Vantra", "Parallax", "Northstar"] },
  { surface: "Google AI Overviews", status: "MENTION", firstChoice: false, named: ["Vantra", "Lumen", "Northstar"] },
]

export interface BrandRank {
  name: string
  isYou?: boolean
  appearedIn: number
  firstChoiceIn: number
}

// Ranked by presence strength across SURFACE_ANSWERS (PRIMARY/first-choice
// weight first), not raw appearance count — Northstar and Vantra both
// appear on 5 of 5 surfaces, but Vantra is first choice on 4 of them vs.
// Northstar's 1, which is what keeps Vantra + Lumen ahead of Northstar
// (DEMO_WORKSPACE.competitorsAhead = 2).
export const BRAND_RANKS: BrandRank[] = [
  { name: "Vantra", appearedIn: 5, firstChoiceIn: 4 },
  { name: "Lumen", appearedIn: 4, firstChoiceIn: 0 },
  { name: DEMO_WORKSPACE.brand, isYou: true, appearedIn: 5, firstChoiceIn: 1 },
  { name: "Fieldstone", appearedIn: 2, firstChoiceIn: 0 },
  { name: "Parallax", appearedIn: 1, firstChoiceIn: 0 },
]

export interface CitedSource {
  domain: string
  title: string
}

// 12 sources = DEMO_WORKSPACE.citedSources. Fictional domains/titles —
// illustrative only, consistent with the "Product view · Demo workspace" frame.
export const CITED_SOURCES: CitedSource[] = [
  { domain: "retailstackreview.com", title: "Best customer data platforms for multi-brand retail, 2026" },
  { domain: "cdpbuyersnotes.com", title: "CDP shortlist: what multi-brand teams actually choose" },
  { domain: "dataopsforum.co", title: "Community thread: a CDP for 5+ retail brands under one roof" },
  { domain: "composablestack.guide", title: "The composable CDP stack, explained" },
  { domain: "martechledger.com", title: "2026 multi-brand retail martech report" },
  { domain: "vantra.io", title: "Vantra customer stories" },
  { domain: "lumendata.io", title: "Lumen vs. the category" },
  { domain: "peerlist-reviews.com", title: "Verified user reviews: customer data platforms" },
  { domain: "retailops-weekly.com", title: "Newsletter: choosing a CDP after brand consolidation" },
  { domain: "northstar.io", title: "Why Northstar for multi-brand retail" },
  { domain: "analystbrief.co", title: "Independent analyst brief: the CDP category, 2026" },
  { domain: "opsforum.net", title: "Forum thread: switching CDPs after an acquisition" },
]
